import './styles.scoped.css'
import {useEffect, useState} from 'react'
import {Typography, Button, TextField, Modal, IconButton, FormControl, Select, MenuItem} from '@mui/material'
import {Close, Add, Delete} from '@mui/icons-material'
import {useForm, useFieldArray, Controller} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {phoneRegex, emailRegex, parseError} from '../../../../utils/helpers'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {clientApi} from '../../../../services'
import {useMobile} from '../../../../hooks'
import {useParams} from 'react-router-dom'

export default ({open, setOpen, data, loadData}) => {
    const {setLoading, setMessageBox, branchId} = useGlobalStates()
    const {mobile} = useMobile()
    const {clientId} = useParams()
    const [oldPrimaryPhoneNumber, setOldPrimaryPhoneNumber] = useState('')
    const form = useForm({
        resolver: yupResolver(
            yup.object().shape({
                primaryPhoneNumberType: yup.string().default('mobile'),
                primaryPhoneNumber: yup
                    .string()
                    .required('Phone number is required')
                    .test('is-valid-phone', 'Invalid phone number', value => phoneRegex.test(value)),
                primaryEmailAddressType: yup.string().default('personal'),
                primaryEmailAddress: yup.string().test('email-optional', 'Invalid email address', value => {
                    if (!value || value.trim() === '') return true
                    return emailRegex.test(value)
                }),
                primaryPhoneNumbers: yup.array().of(
                    yup.object().shape({
                        type: yup.string().default('mobile'),
                        number: yup.string().test('phone-optional', 'Invalid phone number', val => {
                            if (!val || val.trim() === '') return true
                            return phoneRegex.test(val)
                        }),
                    }),
                ),
                primaryEmailAddresses: yup.array().of(
                    yup.object().shape({
                        type: yup.string().default('personal'),
                        email: yup.string().test('email-optional', 'Invalid email address', val => {
                            if (!val || val.trim() === '') return true
                            return emailRegex.test(val)
                        }),
                    }),
                ),
            }),
        ),
    })
    const {
        fields: primaryPhoneNumberFields,
        append: appendPrimaryPhoneNumber,
        remove: removePrimaryPhoneNumber,
    } = useFieldArray({control: form?.control, name: 'primaryPhoneNumbers'})
    const {
        fields: primaryEmailAddressFields,
        append: appendPrimaryEmailAddress,
        remove: removePrimaryEmailAddress,
    } = useFieldArray({control: form?.control, name: 'primaryEmailAddresses'})
    const primaryPhoneNumberTypes = [
        {key: 'mobile', value: 'Mobile'},
        {key: 'work', value: 'Work'},
        {key: 'home', value: 'Home'},
    ]
    const primaryEmailAddressTypes = [
        {key: 'personal', value: 'Personal'},
        {key: 'work', value: 'Work'},
    ]

    useEffect(() => {
        form.reset()
        if (data) {
            setOldPrimaryPhoneNumber(data?.phones?.find(item => item.is_primary)?.number || '')

            form.setValue('primaryPhoneNumberType', data?.phones?.find(item => item.is_primary)?.type)
            form.setValue('primaryPhoneNumber', data?.phones?.find(item => item.is_primary)?.number)
            data?.phones?.map(phone => {
                if (!phone?.is_primary) {
                    appendPrimaryPhoneNumber({type: phone?.type, number: phone?.number})
                }
            })
            form.setValue('primaryEmailAddressType', data?.emails?.find(item => item.is_primary)?.type)
            form.setValue('primaryEmailAddress', data?.emails?.find(item => item.is_primary)?.email)
            data?.emails?.map(email => {
                if (!email?.is_primary) {
                    appendPrimaryEmailAddress({type: email?.type, email: email?.email})
                }
            })
        }
    }, [data])

    const submit = formData => {
        let primaryPhoneNumbers = []
        formData?.primaryPhoneNumbers?.map(item =>
            primaryPhoneNumbers.push({
                type: item?.type,
                number: item?.number,
                is_primary: false,
            }),
        )
        let primaryEmailAddresses = []
        formData?.primaryEmailAddresses?.map(item =>
            primaryEmailAddresses.push({
                type: item?.type,
                email: item?.email,
                is_primary: false,
            }),
        )
        const body = {
            phones: [
                {
                    type: formData?.primaryPhoneNumberType,
                    number: formData?.primaryPhoneNumber,
                    is_primary: true,
                },
                ...primaryPhoneNumbers,
            ],
            emails: [
                {
                    type: formData?.primaryEmailAddressType,
                    email: formData?.primaryEmailAddress,
                    is_primary: true,
                },
                ...primaryEmailAddresses,
            ],
        }
        setLoading(true)
        clientApi
            .updateClientContact(clientId, data?.id, body)
            .then(() => {
                setLoading(false)
                setOpen(false)
                setMessageBox({
                    status: 'success',
                    title: 'Client Primary Contacts Updated Successfully',
                    description: `The client’s primary contact details for ${data?.name} have been updated.`,
                })
                loadData()
                if (formData?.primaryPhoneNumber !== oldPrimaryPhoneNumber) {
                    updateClientPrimaryPhoneNumber(formData?.primaryPhoneNumber)
                }
            })
            .catch(error => {
                setLoading(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    const updateClientPrimaryPhoneNumber = newPrimaryPhoneNumber => {
        clientApi
            .updateBranchClient(branchId, clientId, {phone: newPrimaryPhoneNumber})
            .then(() => {})
            .catch(error => {
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    return (
        <Modal open={open}>
            <div className="container">
                <div className="content">
                    {mobile ? (
                        <>
                            <div className="header">
                                <Typography variant="h6" color="text.primary">
                                    Update Client Contacts
                                </Typography>
                                <IconButton onClick={() => setOpen(false)}>
                                    <Close />
                                </IconButton>
                            </div>
                            <div className="actions">
                                <Button variant="outlined" onClick={() => setOpen(false)} style={{flex: 1}}>
                                    Cancel
                                </Button>
                                <Button variant="contained" fullWidth onClick={form.handleSubmit(submit)} style={{flex: 1}}>
                                    Update
                                </Button>
                            </div>
                            <br />
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <Typography variant="h6" color="text.primary" className="title">
                            Update Client Contacts
                        </Typography>
                    )}
                    <br />
                    <Typography variant="overline" color="text.secondary">
                        PHONE NUMBER
                    </Typography>
                    <br />
                    <br />
                    <Typography variant="inputLabel" color="text.secondary">
                        Phone Number{' '}
                        <Typography variant="inputLabel" color="error">
                            *
                        </Typography>
                    </Typography>
                    <div className="form">
                        <FormControl style={{minWidth: 155}}>
                            <Controller
                                {...form.register(`primaryPhoneNumberType`)}
                                control={form.control}
                                render={({field}) => (
                                    <Select
                                        {...field}
                                        value={field.value ?? 'mobile'}
                                        onChange={e => field.onChange(e.target.value)}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 300,
                                                },
                                            },
                                        }}>
                                        {primaryPhoneNumberTypes.map(item => (
                                            <MenuItem key={item?.key} value={item?.key}>
                                                {item?.value}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                        </FormControl>
                        <TextField
                            placeholder="Enter your phone number"
                            fullWidth
                            {...form.register('primaryPhoneNumber')}
                            error={!!form.formState.errors['primaryPhoneNumber']}
                            helperText={form.formState.errors['primaryPhoneNumber']?.message}
                        />
                    </div>
                    {primaryPhoneNumberFields.map((field, index) => (
                        <div key={field.id} style={{marginTop: -14}}>
                            <br />
                            <br />
                            <Typography variant="inputLabel" color="text.secondary">
                                Phone Number
                            </Typography>
                            <div className="form">
                                <FormControl style={{minWidth: 155}}>
                                    <Controller
                                        {...form.register(`primaryPhoneNumbers.${index}.type`)}
                                        control={form.control}
                                        render={({field}) => (
                                            <Select
                                                {...field}
                                                value={field.value ?? 'mobile'}
                                                onChange={e => field.onChange(e.target.value)}
                                                MenuProps={{
                                                    PaperProps: {
                                                        style: {
                                                            maxHeight: 300,
                                                        },
                                                    },
                                                }}>
                                                {primaryPhoneNumberTypes.map(item => (
                                                    <MenuItem key={item?.key} value={item?.key}>
                                                        {item?.value}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                </FormControl>
                                <TextField
                                    placeholder="Enter your phone number"
                                    fullWidth
                                    {...form.register(`primaryPhoneNumbers.${index}.number`)}
                                    error={!!form.formState.errors?.primaryPhoneNumbers?.[index]?.number}
                                    helperText={form.formState.errors?.primaryPhoneNumbers?.[index]?.number?.message}
                                />
                                <IconButton onClick={() => removePrimaryPhoneNumber(index)}>
                                    <Delete color="error" />
                                </IconButton>
                            </div>
                        </div>
                    ))}
                    <br />
                    <br />
                    <Button variant="text" startIcon={<Add />} onClick={() => appendPrimaryPhoneNumber({value: ''})}>
                        Add Phone Number
                    </Button>
                    <br />
                    <br />
                    <Typography variant="overline" color="text.secondary">
                        EMAIL
                    </Typography>
                    <br />
                    <br />
                    <Typography variant="inputLabel" color="text.secondary">
                        Email Address
                    </Typography>
                    <div className="form">
                        <FormControl style={{minWidth: 155}}>
                            <Controller
                                {...form.register(`primaryEmailAddressType`)}
                                control={form.control}
                                render={({field}) => (
                                    <Select
                                        {...field}
                                        value={field.value ?? 'personal'}
                                        onChange={e => field.onChange(e.target.value)}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 300,
                                                },
                                            },
                                        }}>
                                        {primaryEmailAddressTypes.map(item => (
                                            <MenuItem key={item?.key} value={item?.key}>
                                                {item?.value}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                        </FormControl>
                        <TextField
                            placeholder="Enter your email address"
                            fullWidth
                            {...form.register('primaryEmailAddress')}
                            error={!!form.formState.errors['primaryEmailAddress']}
                            helperText={form.formState.errors['primaryEmailAddress']?.message}
                        />
                    </div>
                    {primaryEmailAddressFields.map((field, index) => (
                        <div key={field.id} style={{marginTop: -14}}>
                            <br />
                            <br />
                            <Typography variant="inputLabel" color="text.secondary">
                                Email Address
                            </Typography>
                            <div className="form">
                                <FormControl style={{minWidth: 155}}>
                                    <Controller
                                        {...form.register(`primaryEmailAddresses.${index}.type`)}
                                        control={form.control}
                                        render={({field}) => (
                                            <Select
                                                {...field}
                                                value={field.value ?? 'personal'}
                                                onChange={e => field.onChange(e.target.value)}
                                                MenuProps={{
                                                    PaperProps: {
                                                        style: {
                                                            maxHeight: 300,
                                                        },
                                                    },
                                                }}>
                                                {primaryEmailAddressTypes.map(item => (
                                                    <MenuItem key={item?.key} value={item?.key}>
                                                        {item?.value}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                </FormControl>
                                <TextField
                                    placeholder="Enter your email address"
                                    fullWidth
                                    {...form.register(`primaryEmailAddresses.${index}.email`)}
                                    error={!!form.formState.errors?.primaryEmailAddresses?.[index]?.email}
                                    helperText={form.formState.errors?.primaryEmailAddresses?.[index]?.email?.message}
                                />
                                <IconButton onClick={() => removePrimaryEmailAddress(index)}>
                                    <Delete color="error" />
                                </IconButton>
                            </div>
                        </div>
                    ))}
                    <br />
                    <br />
                    <Button variant="text" startIcon={<Add />} onClick={() => appendPrimaryEmailAddress({value: ''})}>
                        Add Email
                    </Button>
                    <br />
                    <br />
                    {mobile ? (
                        <>
                            <br />
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <div className="actions">
                            <Button onClick={() => setOpen(false)} variant="outlined" fullWidth>
                                Cancel
                            </Button>
                            <Button variant="contained" fullWidth onClick={form.handleSubmit(submit)}>
                                Update
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    )
}
